import { z } from "zod";

// ============================================================================
// CLIENT GROUP MIGRATION SCHEMAS & TYPES
// ============================================================================

// Applicant info for migration preview
export const migrationApplicantSchema = z.object({
  applicantId: z.string().uuid(),
  applicantType: z.enum(["contact", "account"]),
  applicantRole: z.enum(["primary_applicant", "co_applicant", "guarantor"]),
});

// Request schema for migration preview
export const clientGroupMigrationPreviewRequestSchema = z.object({
  applicants: z.array(migrationApplicantSchema).min(1),
});

// Response schema for grouped applicants by client group
export const clientGroupApplicantInfoSchema = z.object({
  applicantId: z.string().uuid(),
  applicantType: z.enum(["contact", "account"]),
  applicantRole: z.enum(["primary_applicant", "co_applicant", "guarantor"]),
  name: z.string(),
  clientGroupId: z.string().uuid(),
  clientGroupName: z.string(),
});

// Affected record counts for a client group
export const affectedRecordCountsSchema = z.object({
  contacts: z.number(),
  accounts: z.number(),
  deals: z.number(),
  directors: z.number(),
  shareholders: z.number(),
  trustees: z.number(),
});

// Client group with its applicants and affected counts
export const clientGroupWithApplicantsSchema = z.object({
  clientGroupId: z.string().uuid(),
  clientGroupName: z.string(),
  applicants: z.array(clientGroupApplicantInfoSchema),
  affectedCounts: affectedRecordCountsSchema,
});

// Response schema for migration preview
export const clientGroupMigrationPreviewResponseSchema = z.object({
  hasConflict: z.boolean(),
  clientGroups: z.array(clientGroupWithApplicantsSchema),
  totalAffectedRecords: affectedRecordCountsSchema,
});

// Request schema for migration execution
export const clientGroupMigrationExecuteRequestSchema = z.object({
  masterClientGroupId: z.string().uuid(),
  sourceClientGroupIds: z.array(z.string().uuid()).min(1),
});

// Summary of migrated records
export const migrationSummarySchema = z.object({
  contactsMigrated: z.number(),
  accountsMigrated: z.number(),
  dealsMigrated: z.number(),
  directorsMigrated: z.number(),
  shareholdersMigrated: z.number(),
  trusteesMigrated: z.number(),
  auditLogEntries: z.number(),
  migrationBatchId: z.string().uuid(),
});

// Response schema for migration execution
export const clientGroupMigrationExecuteResponseSchema = z.object({
  success: z.boolean(),
  masterClientGroupId: z.string().uuid(),
  masterClientGroupName: z.string(),
  summary: migrationSummarySchema,
});

// Migration Types
export type MigrationApplicant = z.infer<typeof migrationApplicantSchema>;
export type ClientGroupMigrationPreviewRequest = z.infer<typeof clientGroupMigrationPreviewRequestSchema>;
export type ClientGroupApplicantInfo = z.infer<typeof clientGroupApplicantInfoSchema>;
export type AffectedRecordCounts = z.infer<typeof affectedRecordCountsSchema>;
export type ClientGroupWithApplicants = z.infer<typeof clientGroupWithApplicantsSchema>;
export type ClientGroupMigrationPreviewResponse = z.infer<typeof clientGroupMigrationPreviewResponseSchema>;
export type ClientGroupMigrationExecuteRequest = z.infer<typeof clientGroupMigrationExecuteRequestSchema>;
export type MigrationSummary = z.infer<typeof migrationSummarySchema>;
export type ClientGroupMigrationExecuteResponse = z.infer<typeof clientGroupMigrationExecuteResponseSchema>;
